import Backbone from 'backbone';
import { renderSections, getSectionInnerHTML } from '@/lib/renderSections';
import { PUB_SUB_EVENTS, subscribe, publish } from '@/lib/pubsub';
import OneMarketing from '@/modules/module-oneMarketing';

const OneCartView = Backbone.View.extend({

    initialize: function (settings) {
        subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.handleCartUpdate.bind(this)
        );

        if (ORW.GWP && ORW.GWP.enable == `true`) {
            window.addEventListener("gwp_gift_added", this.handleCartUpdate.bind(this))
            window.addEventListener("gwp_gift_removed", this.handleCartUpdate.bind(this))
            window.addEventListener("gwp_gift_updated", this.handleCartUpdate.bind(this))
            window.addEventListener("gwp_gift_swap_updated", this.handleCartUpdate.bind(this))
        }

        if (ORW.SP && ORW.SP.enable == `true`) {
            window.addEventListener("sp_added", this.handleCartUpdate.bind(this))
            window.addEventListener("sp_removed", this.handleCartUpdate.bind(this))
            window.addEventListener("sp_updated", this.handleCartUpdate.bind(this))
        }

        // Education Kit listeners
        window.addEventListener("edu_kit_removed", this.handleCartUpdate.bind(this))
        window.addEventListener("edu_kit_quantity_synced", this.handleCartUpdate.bind(this))

        if (ORW.enableGTM) {
            ORW.marketing = ORW.marketing || new OneMarketing;
            ORW.marketing.initCartPageWatchers();
        }
        
        this.handleCartRecsAdd();
    },

    events: {
        'click .item-remove': 'handleRemoveItem',
        'change .item-quantity input': 'handleQuantityChange',
        'click .item-quantity button': 'handleQuantityButtonClick',
    },

    /**
     * Request updated cart HTML from Shopify Section Rendering API and replace the
     * current cart HTML with the new HTML
     */
    handleCartUpdate: async function() {
        const res = await renderSections(['cart']);

        const cartItemsHTML = getSectionInnerHTML(res.cart, '.cart-items');
        this.el.querySelector('.cart-items').innerHTML = cartItemsHTML;

        const subtotalCartHTML = getSectionInnerHTML(res.cart, '.subtotal.cart-only');
        this.el.querySelector('.subtotal.cart-only').innerHTML = subtotalCartHTML;  

        const afterPayHTML = getSectionInnerHTML(res.cart, '.afterpay-msg-wrapper-cart');
        this.el.querySelector('.afterpay-msg-wrapper-cart').innerHTML = afterPayHTML;

        const subtotalHTML = getSectionInnerHTML(res.cart, '.total');
        this.el.querySelector('.total').innerHTML = subtotalHTML;       

        const countHtml = getSectionInnerHTML(res.cart, '.cart-items-size');
        document.querySelectorAll('.minicart-count').forEach(el => {
            el.innerHTML = countHtml;
        });

        this.updateProgressBar(res);

        // Update nosto cart & recs
        if( window.Nosto ) { window.Nosto.reloadCart(); }
        if( initNostoCartRecs && typeof initNostoCartRecs == 'function') { setTimeout(initNostoCartRecs, 500); }

        if (this.error) {
            const { id, message } = this.error;
            this.showError(id, message);;
        }

        // Check bridal kit discount after cart update
        if (window.BridalKitDiscount) {
            window.BridalKitDiscount.check();
        }
    },

    handleCartRecsAdd: async function() {
        this.el.addEventListener('click', async (event) => {
            if (event.target.matches('.ncr-atc-btn')) {
                event.preventDefault();
                let variant_id = event.target.getAttribute('data-variant');
                let edu_kit_variant_id = event.target.getAttribute('data-edu-kit-variant-id');
                
                let items = [];
                
                if (edu_kit_variant_id) {
                    // Education product - add both product + kit with unique link ID
                    const educationLinkId = crypto.randomUUID();
                    
                    items.push({
                        id: variant_id,
                        quantity: 1,
                        properties: {
                            '_education_link_id': educationLinkId
                        }
                    });
                    
                    items.push({
                        id: edu_kit_variant_id,
                        quantity: 1,
                        properties: {
                            '_linked_education': variant_id,
                            '_education_link_id': educationLinkId,
                            '_EDU_KIT': 'true'
                        }
                    });
                } else {
                    // Regular product
                    items.push({ id: variant_id, quantity: 1 });
                }
                
                await fetch('/cart/add.js', { 
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: items })  
                })
                .then(response => response.json())
                .then(cart => {
                    if (event.target.getAttribute('data-ns-ref') || event.target.getAttribute('data-ns-reftype') && nostojs) {
                        nostojs(api => {
                            api.recordAttribution({ 
                                type: "cp", 
                                target: event.target.getAttribute('data-ns-pid'),
                                ref: event.target.getAttribute('data-ns-ref'),
                                refType: event.target.getAttribute('data-ns-reftype'),
                            })
                            .done()
                        });
                    }
                    publish(PUB_SUB_EVENTS.cartUpdate, {});
                })
                .catch(error => console.error('Error adding cart recs:', error) );
            }
        });
    },

    updateProgressBar: async function(res) {
        if (!res) return;
        const progressBarPercentage = getSectionInnerHTML(res.cart, '.progress-bar-percentage');
        const progressBarMessage = getSectionInnerHTML(res.cart, '.progress-bar-message');
        const progressBar = this.el.querySelectorAll('.progress-bar');
    
        this.el.querySelectorAll('.progress-bar-message').forEach(el => {
            el.innerHTML = progressBarMessage;
        });

        this.el.querySelectorAll('.progress-bar').forEach(el => {
            el.style.transition = 'width 1.2s';
            el.style.width = `${progressBarPercentage}%`;
        });
    },

    /**
     * Handle click events on "remove item" buttons
     * @param {Event} e A click event
     */
    handleRemoveItem: async function(e) {
        e.preventDefault();       
        // DD-2596 Check if the clicked item is a GWP product and prevent auto re-addition 
        const targetEl = e.target
        if(targetEl.hasAttribute('data-gwp-product')){
           sessionStorage.setItem('disallow_auto_gwp_add',"true") 
        }
        
        const cartItemElem = e.currentTarget.closest('.cart-item');
        
        // Check if this is an education product with linked kit
        if(cartItemElem.hasAttribute('data-education-link-id')){
            const educationLinkId = cartItemElem.getAttribute('data-education-link-id');
            const productKey = cartItemElem.dataset.key;
            
            // Call OneEduKit to handle batched removal (product + kit in single API call)
            if (window.ORW && window.ORW.eduKit) {
                await window.ORW.eduKit.removeEducationProductWithKit(productKey, educationLinkId);
                
                if (ORW.enableGTM) {
                    ORW.marketing.removeFromCartPage(e.currentTarget);
                }
                return;
            }
        }
        
        // Regular item removal
        this.updateQuantity(cartItemElem.dataset.key, 0);
        if (ORW.enableGTM) {
            ORW.marketing.removeFromCartPage(e.currentTarget);
        }
    },

    /**
     * Start timer to send update request and reset timer if another change event
     * occurs before the timer is up
     * @param {Event} e A change event
     */
    handleQuantityChange: function(e) {
        if (this.timeout) clearTimeout(this.timeout)
        if (ORW.enableGTM) {
            console.log('handleQuantityChange ORW.enableGTM');
            ORW.marketing.checkItemQtyChangeCartPage(e.currentTarget);
        }
        
        const cartItem = e.currentTarget.closest('.cart-item');
        const educationLinkId = cartItem?.getAttribute('data-education-link-id');
        const isEduKit = cartItem?.getAttribute('data-edu-kit') === 'true';
        
        const onTimeout = _ => {
            // Check if this is an education product (not the kit itself)
            if (educationLinkId && !isEduKit && window.ORW && window.ORW.eduKit) {
                const productKey = cartItem.dataset.key;
                const newQuantity = parseInt(e.currentTarget.value);
                window.ORW.eduKit.updateEducationProductWithKit(productKey, educationLinkId, newQuantity);
            } else {
                // Regular item quantity update
                this.updateQuantity(e.currentTarget.dataset.id, e.currentTarget.value);
            }
        };
        this.timeout = setTimeout(onTimeout.bind(this), 1000);
    },

    /**
     * Request to update cart item quantity and update relevant info
     * @param {String} id The cart item's variant ID
     * @param {Number} quantity The cart item's new quantity
     */
    updateQuantity: async function(id, quantity) {
        //this.setLoading(id);
        const res = await fetch(ORW.routes.cartChangeURL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: `application/json`
            },
            body: JSON.stringify({ id, quantity }),
        });
        const cart = await res.json();
        if (cart.errors) {
            this.error = {
                id,
                message: cart.errors.trim()
            };
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {});
    },

    /**
     * Update the quantity input value based on the button clicked and dispatch a
     * change event
     * @param {Event} e A click event
     */
    handleQuantityButtonClick: function(e) {
        const input = e.currentTarget.closest('.item-quantity').querySelector('input');
        e.currentTarget.name === 'plus' ? input.stepUp() : input.stepDown();
        input.dispatchEvent(new Event('change', { bubbles: true }));
    },

    /**
     * Hide prices and show loading spinner for a cart item and the cart subtotal
     * @param {String} id The line item ID of the cart item
     */
    setLoading: function(id) {
        const subtotal = this.el.querySelector('.total-price');
        subtotal.querySelector('span').style.display = 'none';
        subtotal.querySelector('.spinner').style.display = 'block';

        const item = this.el.querySelector(`[data-id="${id}"]`);
        const itemTotals = item.querySelectorAll('.item-total span');
        itemTotals.forEach(span => {
            span.style.display = 'none';
        });
        item.querySelector('.item-total .spinner').style.display = 'block';
    },

    showError: function(id, message) {
        const item = this.el.querySelector(`[data-id="${id}"]`);
        const error = item.querySelector('.item-error');
        console.log(error, message)
        error.textContent = message;
    },

    destroy: function() {
        this.unsubscribe();
    }
});

export default OneCartView;